import { useEffect, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router';
import { CMSService } from 'client/services';
import { setMessages } from 'client/store/systemMessageSlice/systemMessageSlice';
import { logWithPrefix } from 'client/utils/logging';

const log = logWithPrefix('systemMessageSlice');

/**
 * Custom hook to load system messages from CMS
 * This centralizes the system message loading logic that was previously duplicated
 * across multiple components (HomePage, MapPage, etc.).
 * The function fetches messages on mount and whenever the route changes whilst respecting the cache timeout.
 *
 * @param {Object} options - Configuration options
 * @param {boolean} options.loadOnMount - Whether to load messages when the hook mounts (default: true)
 * @returns {Function} loadSystemMessages - Function to manually trigger loading
 */
const useSystemMessages = ({ loadOnMount = true } = {}) => {
    const dispatch = useDispatch();
    const location = useLocation();
    const isLoadingRef = useRef(false);

    const loadSystemMessages = useCallback(async () => {
        if (isLoadingRef.current) {
            return;
        }
        isLoadingRef.current = true;
        try {
            const messages = await CMSService.getDisplayMessages();
            dispatch(setMessages(messages));
        } catch (error) {
            log('Failed to load system messages from CMS:', error);
        } finally {
            isLoadingRef.current = false;
        }
    }, [dispatch]);

    // Load system messages on mount and route changes
    useEffect(() => {
        if (loadOnMount) {
            loadSystemMessages();
        }
    }, [loadOnMount, location.pathname, loadSystemMessages]);

    return loadSystemMessages;
};

export default useSystemMessages;
